// MfaChallenge — split-pane factor picker shared by:
//   1. /portal/mfa/challenge        (standalone re-challenge after the
//      12h freshness window expires).
//   2. /users/sign_in?mfa=1         (inline challenge on the SignIn page
//      when password auth succeeds but the user still owes a 2nd factor).
//
// Layout: a left rail of factor "tabs" (only shown for factors the user
// has wired) + a centered right pane with the factor-specific UI. Active
// tab gets the red glow border. On narrow viewports the rail collapses
// into a horizontal pill row stacked above the pane.
//
// Endpoints (unchanged from the old design):
//   - Passkey       → POSTs `{ credential }` to /portal/mfa/challenge/passkey
//   - Authenticator → POSTs `{ code }`       to /portal/mfa/challenge/totp
//   - Recovery code → POSTs `{ code }`       to /portal/mfa/challenge/recovery_code
// All three redirect to /dashboard on success and back to the challenge
// page (with a flash) on failure.

import { useState } from "react"
import type { ReactNode } from "react"
import { router } from "@inertiajs/react"
import { ArrowRight, ChevronRight, KeyRound, LifeBuoy, ShieldCheck } from "lucide-react"
import { Card } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { cn } from "@/lib/cn"
import { verifyPasskey } from "@/lib/webauthn"

export type MfaChallengeOptions = {
  hasPasskey:     boolean
  hasTotp:        boolean
  hasRecovery:    boolean
  passkeyOptions: Record<string, unknown> | null
}

type Props = MfaChallengeOptions

type Tab = "passkey" | "totp" | "recovery"

type TabDef = {
  key:   Tab
  label: string
  icon:  ReactNode
}

export function MfaChallenge({ hasPasskey, hasTotp, hasRecovery, passkeyOptions }: Props) {
  const initialTab: Tab = hasPasskey ? "passkey" : (hasTotp ? "totp" : "recovery")
  const [tab, setTab] = useState<Tab>(initialTab)

  const allTabs: Array<TabDef | null> = [
    hasPasskey  ? { key: "passkey",  label: "Passkey",            icon: <KeyRound size={18} /> }   : null,
    hasTotp     ? { key: "totp",     label: "Authenticator app",  icon: <ShieldCheck size={18} /> } : null,
    hasRecovery ? { key: "recovery", label: "Recovery code",      icon: <LifeBuoy size={18} /> }   : null,
  ]
  const tabs: TabDef[] = allTabs.filter((t): t is TabDef => t !== null)

  return (
    <Card className="overflow-hidden p-0">
      <div className="flex flex-col md:grid md:grid-cols-[280px_1fr]">
        {/* Left rail / top pills */}
        <aside className="border-b border-border bg-app-panel2/50 p-3 md:border-b-0 md:border-r md:p-4">
          <div className="flex flex-row gap-2 overflow-x-auto md:flex-col">
            {tabs.map((t) => (
              <FactorTab
                key={t.key}
                def={t}
                active={t.key === tab}
                onClick={() => setTab(t.key)}
              />
            ))}
          </div>
        </aside>

        {/* Right pane */}
        <div className="flex min-h-[360px] items-center justify-center p-6 md:p-10">
          <div className="w-full max-w-sm">
            {tab === "passkey"  && <PasskeyPanel options={passkeyOptions} />}
            {tab === "totp"     && <TotpPanel />}
            {tab === "recovery" && <RecoveryPanel />}
          </div>
        </div>
      </div>
    </Card>
  )
}

function FactorTab({ def, active, onClick }: { def: TabDef, active: boolean, onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "group flex w-full shrink-0 items-center gap-3 rounded-control px-3 py-3 text-left text-sm transition",
        active
          ? "border border-brand-red bg-brand-redSoft text-app-text shadow-redGlow"
          : "border border-transparent text-app-muted hover:border-border hover:bg-white/5 hover:text-app-text",
      )}
      aria-pressed={active}
    >
      <span
        className={cn(
          "flex h-8 w-8 shrink-0 items-center justify-center rounded-control border",
          active ? "border-brand-red/40 bg-brand-redSoft text-brand-red" : "border-border bg-white/5 text-app-muted",
        )}
      >
        {def.icon}
      </span>
      <span className="flex-1 truncate font-medium">{def.label}</span>
      <ChevronRight size={16} className={cn("hidden md:inline", active ? "text-brand-red" : "text-app-subtle")} />
    </button>
  )
}

function PaneShell({ icon, title, description, children }: {
  icon:        ReactNode
  title:       string
  description: string
  children:    ReactNode
}) {
  return (
    <div className="flex flex-col items-center text-center">
      <div className="mb-5 flex h-16 w-16 items-center justify-center rounded-full border border-border bg-white/5 text-app-text">
        {icon}
      </div>
      <h2 className="text-xl font-semibold text-app-text">{title}</h2>
      <p className="mt-2 text-sm text-app-muted">{description}</p>
      <div className="mt-6 w-full">{children}</div>
    </div>
  )
}

function PasskeyPanel({ options }: { options: Record<string, unknown> | null }) {
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function go() {
    if (!options) {
      setError("No passkey on file. Try a different factor.")
      return
    }
    setSubmitting(true)
    setError(null)
    try {
      const credential = await verifyPasskey(options as Parameters<typeof verifyPasskey>[0])
      // Inertia's FormDataConvertible doesn't include a plain Record;
      // the credential JSON is a nested object the server JSON-decodes,
      // so we tunnel it through the type system as `unknown`.
      router.post("/portal/mfa/challenge/passkey", { credential } as unknown as Record<string, never>)
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <PaneShell
      icon={<KeyRound size={28} />}
      title="Use a passkey"
      description="Approve with Face ID, Touch ID, Windows Hello, a security key, or your phone."
    >
      {error && <p className="mb-3 text-sm text-status-error">{error}</p>}
      <Button onClick={go} disabled={submitting} className="w-full gap-2">
        {submitting ? "Waiting for browser…" : "Continue with passkey"}
        {!submitting && <ArrowRight size={16} />}
      </Button>
    </PaneShell>
  )
}

function TotpPanel() {
  const [code, setCode] = useState("")

  function submit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault()
    router.post("/portal/mfa/challenge/totp", { code })
  }

  return (
    <PaneShell
      icon={<ShieldCheck size={28} />}
      title="Enter your code"
      description="Open your authenticator app and enter the 6-digit code."
    >
      <form onSubmit={submit} className="space-y-4">
        <Input
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
          inputMode="numeric"
          pattern="[0-9]*"
          autoComplete="one-time-code"
          maxLength={6}
          required
          autoFocus
          placeholder="123456"
          className="w-full text-center text-lg tracking-[0.5em]"
        />
        <Button type="submit" className="w-full">Verify</Button>
      </form>
    </PaneShell>
  )
}

function RecoveryPanel() {
  const [code, setCode] = useState("")

  function submit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault()
    router.post("/portal/mfa/challenge/recovery_code", { code })
  }

  return (
    <PaneShell
      icon={<LifeBuoy size={28} />}
      title="Use a recovery code"
      description="One of the 8 single-use codes you saved when you enrolled."
    >
      <form onSubmit={submit} className="space-y-4">
        <Input
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
          required
          maxLength={32}
          autoFocus
          placeholder="XXXX-XXXX"
          className="w-full text-center tracking-widest"
        />
        <Button type="submit" className="w-full">Verify code</Button>
      </form>
    </PaneShell>
  )
}
